import { Injector, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DeepSignal } from '@ngrx/signals';
import { disableNavLinks, NavBarDataInterface } from '@simOn/common/containers';
import {
  BaseIoTProviderConnectionDTO,
  MaximoConnection,
  TIntegrationTypes,
  TIotTypes,
  TProviderConnectionInterface,
  TScanTypes
} from '@simOn/provider/connection/models';
import { PublicLinkService } from '@simOn/public-link';
import { isDashboardTabAllowed, publicLinkUnavailableTooltip } from '@simOn/public-link/models';
import { combineLatest, map } from 'rxjs';

export const DASHBOARD_PAGE_DATA: NavBarDataInterface = {
  headerTitle: $localize`:@@DASHBOARD_MAIN_TITLE:Dashboard`,
  shortTitle: $localize`:@@DASHBOARD_MAIN_TITLE:Dashboard`,
  navLinks: [
    { label: $localize`:@@DASHBOARD_ASSETS:Assets`, name: `Assets`, path: 'assets', icon: 'asset' },
    { label: $localize`:@@OVERLAY_MENU_TASKS:Tasks`, name: `Tasks`, path: 'tasks', icon: 'task' },
    { label: $localize`:@@OVERLAY_MENU_EVENTS:Events`, name: `Events`, path: 'events', icon: 'clock' },
    { label: $localize`:@@DASHBOARD_DEVICES:Devices`, name: `Devices`, path: 'devices', icon: 'light_bulb' },
    { label: $localize`:@@DASHBOARD_SCENES:Scenes`, name: `Scenes`, path: 'scenes', icon: 'scenes' },
    {
      label: $localize`:@@DASHBOARD_RESOURCES:Resources`,
      name: `Resources`,
      path: 'resources/maximo-links',
      icon: 'list'
    }
  ],
  activeLinkIndex: 0
};

export function DASHBOARD_PAGE_DATA_WITH_PRIVILEGES(
  providers: {
    providers: DeepSignal<{
      iot: { selected: TIotTypes | undefined; items: TIotTypes[]; isLoaded: boolean };
      integration: { selected: TIntegrationTypes | undefined; items: TIntegrationTypes[]; isLoaded: boolean };
      scan: { selected: TScanTypes | undefined; items: TScanTypes[]; isLoaded: boolean };
    }>;
    isLoaded: Signal<boolean>;
    getIotProviders: Signal<BaseIoTProviderConnectionDTO[]>;
    hasIotHub: Signal<boolean>;
    getIntegrationProviders: Signal<MaximoConnection[]>;
    getScanProviders: Signal<TScanTypes[]>;
    iotProvidersLoaded: Signal<boolean>;
    integrationProvidersLoaded: Signal<boolean>;
    scanProvidersLoaded: Signal<boolean>;
    isMaximoConnected: Signal<boolean>;
    providersLoaded: Signal<boolean>;
    loadAllProviders: (items: TProviderConnectionInterface[]) => void;
    loadIoT: (items: TIotTypes[]) => void;
    loadIntegration: (items: TIntegrationTypes[]) => void;
    loadScan: (items: TScanTypes[]) => void;
    removeIot: (item: TIotTypes) => void;
    removeIntegration: (item: TIntegrationTypes) => void;
    removeScan: (item: TScanTypes) => void;
  } & import('@ngrx/signals').StateSource<{
    providers: {
      iot: { selected: TIotTypes | undefined; items: TIotTypes[]; isLoaded: boolean };
      integration: { selected: TIntegrationTypes | undefined; items: TIntegrationTypes[]; isLoaded: boolean };
      scan: { selected: TScanTypes | undefined; items: TScanTypes[]; isLoaded: boolean };
    };
    isLoaded: boolean;
  }>,
  publicLinkService: PublicLinkService,
  injector: Injector
) {
  const navBar = { ...DASHBOARD_PAGE_DATA };
  const hubRequired = $localize`:@@IOT_HUB_REQUIRED_TOOLTIP:Connect an IoT hub to access devices and scenes.`;
  return combineLatest([
    toObservable(providers.isMaximoConnected, { injector }),
    toObservable(providers.hasIotHub, { injector }),
    publicLinkService.isPublicLinkModeActive$,
    // re-evaluates when a JWT public link session or its capabilities change
    toObservable(publicLinkService.capabilities, { injector })
  ]).pipe(
    map(([canActiveResources, hasIotHub, isPublicLinkModeActive]) => {
      const publicLinkAccess = publicLinkService.access();
      const visibleLinks = navBar.navLinks.filter((navLink) => {
        // Resources depends on a Maximo connection (the owner's setup, not a product feature), so visitors never see it
        if (navLink.name === 'Resources' && (!canActiveResources || isPublicLinkModeActive)) return false;
        // public link viewers cannot connect a hub, so hub-dependent tabs are hidden for them instead of disabled
        if ((navLink.name === 'Devices' || navLink.name === 'Scenes') && isPublicLinkModeActive && !hasIotHub) {
          return false;
        }
        return true;
      });
      // tabs the link does not cover stay visible but disabled, so a visitor sees what the product offers
      const lockedByLink = visibleLinks
        .filter((navLink) => !isDashboardTabAllowed(navLink.path, publicLinkAccess))
        .map((navLink) => navLink.name);
      const links = disableNavLinks(visibleLinks, lockedByLink, publicLinkUnavailableTooltip());
      return {
        ...navBar,
        // devices and scenes come from an IoT hub; without one they are shown but disabled
        navLinks: hasIotHub ? links : disableNavLinks(links, ['Devices', 'Scenes'], hubRequired)
      };
    })
  );
}
